import { useEffect } from "react";
import { AppHeader } from "@/components/app-header";
import { AppFooter } from "@/components/app-footer";

export default function TermsPage() {
  useEffect(() => { document.title = "Terms of Service | DanceMirror"; window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <AppHeader />
      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-4 py-8 sm:py-12 space-y-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold" data-testid="text-terms-title">Terms of Service</h1>
            <p className="text-sm text-muted-foreground mt-2" data-testid="text-terms-effective">Effective Date: February 11, 2026</p>
          </div>

          <section className="space-y-4">
            <h2 className="text-lg font-semibold">1. Acceptance of Terms</h2>
            <p className="text-muted-foreground leading-relaxed">
              By accessing or using DanceMirror ("the Service"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use the Service. We reserve the right to update these terms at any time, and your continued use of the Service constitutes acceptance of any changes.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-lg font-semibold">2. Description of Service</h2>
            <p className="text-muted-foreground leading-relaxed">
              DanceMirror is a web-based tool that helps dancers learn choreography by providing mirrored video playback, speed control, A-B looping, and visual left/right orientation guides. The Service supports YouTube video embedding and user-uploaded video files.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-lg font-semibold">3. User Accounts</h2>
            <p className="text-muted-foreground leading-relaxed">
              To access certain features, you must create an account by signing in with Google. You are responsible for maintaining the confidentiality of your account and for all activities that occur under your account. You agree to provide accurate and complete information when creating your account.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-lg font-semibold">4. Subscription Plans and Payments</h2>
            <p className="text-muted-foreground leading-relaxed">
              DanceMirror offers both free and paid subscription tiers. Paid subscriptions are billed through Stripe and provide access to additional features such as speed control, A-B looping, and expanded video upload storage. Subscription fees are charged on a recurring basis (monthly or yearly) and are non-refundable except as required by law. You may cancel your subscription at any time through your account settings, and your access to paid features will continue until the end of your current billing period.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-lg font-semibold">5. Video Uploads and Content</h2>
            <p className="text-muted-foreground leading-relaxed">
              You may upload video files to the Service for personal use. By uploading content, you represent that you have the right to use and upload such content and that it does not violate any third-party rights. Uploaded videos are stored and delivered via Cloudflare Stream. We reserve the right to remove any content that violates these terms or is deemed inappropriate. Maximum upload file size is 500MB per video.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-lg font-semibold">6. YouTube Content</h2>
            <p className="text-muted-foreground leading-relaxed">
              DanceMirror allows you to embed and view YouTube videos within the Service. All YouTube content is subject to YouTube's Terms of Service and is streamed directly from YouTube. DanceMirror does not download, store, or redistribute YouTube videos. You are responsible for ensuring your use of YouTube content complies with YouTube's Terms of Service.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-lg font-semibold">7. Acceptable Use</h2>
            <p className="text-muted-foreground leading-relaxed">
              You agree not to use the Service to: upload or share content that infringes on intellectual property rights; attempt to gain unauthorized access to the Service or its systems; use the Service for any unlawful purpose; interfere with or disrupt the Service or its infrastructure; or redistribute, resell, or sublicense any part of the Service.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-lg font-semibold">8. Intellectual Property</h2>
            <p className="text-muted-foreground leading-relaxed">
              The DanceMirror name, logo, and all related software, designs, and content (excluding user-uploaded content) are the intellectual property of DanceMirror. You retain ownership of any content you upload to the Service. By using the Service, you grant DanceMirror a limited license to process, store, and deliver your uploaded content solely for the purpose of providing the Service to you.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-lg font-semibold">9. Limitation of Liability</h2>
            <p className="text-muted-foreground leading-relaxed">
              The Service is provided "as is" without warranties of any kind, either express or implied. DanceMirror shall not be liable for any indirect, incidental, special, or consequential damages arising out of or in connection with your use of the Service. Our total liability shall not exceed the amount you have paid for the Service in the twelve months preceding the claim.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-lg font-semibold">10. Termination</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may suspend or terminate your account at any time for violations of these terms. You may delete your account at any time by contacting support. Upon termination, your right to use the Service ceases immediately, and your uploaded content may be deleted.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-lg font-semibold">11. Contact</h2>
            <p className="text-muted-foreground leading-relaxed">
              If you have questions about these Terms of Service, please contact us at{" "}
              <a href="mailto:support@payong.io?subject=DanceMirror%20Support" className="text-primary hover:underline" data-testid="link-terms-contact">
                support@payong.io
              </a>.
            </p>
          </section>
        </div>
      </main>
      <AppFooter />
    </div>
  );
}
